import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Flex, Button, useDisclosure } from 'src/components/ui';
import LaunchingCampaignModal from './parts/LaunchingCampaignModal';
import CampaignSuccessModal from './parts/CampaignSuccessModal';
import InsufficientCreditsModal from './parts/InsufficientCreditsModal';
import CampaignLimitModal from './parts/CampaignLimitModal';
import { CampaignContext } from 'src/contexts';
import { validateCampaignSchedule } from '../utils/validations';
import { useLocation } from 'react-router-dom';
import { useMyIntegrations } from 'src/contexts/integration/IntegrationContext';
import {
	ECampaignScheduleType,
	ILaunchResponse,
	ICreative,
	IDesignDirection,
	IFailedCreative,
	IError,
	IPublishedCreative,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import {
	genrateCampaignCreatives,
	getCampaign,
	publishCampaignChannelsAndWait,
} from 'src/services/campaign';
import { delay, placementIdToChannelIdMap } from 'src/lib/utils';
import { externalRedirect } from 'src/lib/utils/redirect';
import { checkCampaignCredits } from 'src/services/billing';
import { countPendingCreativesByChannel } from '../utils/countPendingCreativesByChannel';
import { useForm } from 'react-hook-form';
import { customToast } from 'src/services/toast';

interface LaunchCampaignProps {
	isValidCampaignConfig: boolean;
	isValidCampaignBudget: boolean;
	onLaunch: () => Promise<ILaunchResponse>;
	setShowCreativesLoader: (val: boolean) => void;
	pendingCreativesCount: number;
	designDirections: IDesignDirection[];
	creatives: ICreative[];
	selectedChannels: string[];
	setPendingCreativesCount: React.Dispatch<React.SetStateAction<number>>;
}

const LaunchCampaign: FC<LaunchCampaignProps> = ({
	isValidCampaignConfig,
	isValidCampaignBudget,
	onLaunch,
	selectedChannels = [],
	setShowCreativesLoader,
	pendingCreativesCount,
	designDirections,
	creatives,
	setPendingCreativesCount,
}) => {
	const {
		isOpen: isOpenLaunchingModal,
		onOpen: onOpenLaunchingModal,
		onClose: onCloseLaunchingModal,
	} = useDisclosure();
	const {
		isOpen: isOpenSuccessModal,
		onOpen: onOpenSuccessModal,
		onClose: onCloseSuccessModal,
	} = useDisclosure();
	const {
		isOpen: isOpenInsufficientCreditsModal,
		onOpen: onOpenInsufficientCreditsModal,
		onClose: onCloseInsufficientCreditsModal,
	} = useDisclosure();
	const {
		isOpen: isOpenCampaignLimitModal,
		onOpen: onOpenCampaignLimitModal,
		onClose: onCloseCampaignLimitModal,
	} = useDisclosure();
	const {
		campaign,
		setCampaign,
		setIsLaunching,
		setOpenSections,
		id: campaignId,
		hasFinishedLaunchRef,
		isLaunchingRef,
	} = useContext(CampaignContext);
	const { integrations } = useMyIntegrations();
	const { search } = useLocation();
	const [failedCreatives, setFailedCreatives] = useState<IFailedCreative[]>([]);
	const [publishedCreatives, setPublishedCreatives] = useState<
		IPublishedCreative[]
	>([]);
	const { handleSubmit } = useForm();
	const [isLaunchInitiated, setIsLaunchInitiated] = useState(false);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);
	const [modalTitle, setModalTitle] = useState<
		'SUCCESS' | 'ERROR' | 'PARTIAL PUBLISHED'
	>('SUCCESS');
	const publishButtonRef = useRef<HTMLButtonElement | null>(null);
	const continueLaunchInitiatedRef = useRef(false);
	const [checkoutUrl, setCheckoutUrl] = useState<string>('');
	const [amountRequired, setAmountRequired] = useState<number>(0);
	const [campaignLimitData, setCampaignLimitData] = useState<{
		currentCount?: number;
		limit?: number;
		errorMessage?: string;
		provider?: 'appsumo' | 'stripe';
		planName?: string;
		canUpgrade?: boolean;
		upgradePrice?: number;
		upgradeCurrency?: string;
		nextPlanId?: string;
	}>({});
	const isDesignDirectionPending = designDirections?.some(
		(c) => c.status === 'pending' || c.status === 'generating',
	);
	const isCreativesPending = creatives?.some(
		(c) => c.status === 'pending' || c.status === 'generating',
	);
	const [isPublishButtonLoading, setIsPublishButtonLoading] = useState(false);

	const getCampaignId = (): string | undefined => {
		return campaign?.id;
	};

	// Data for success modal links (no validation logic)
	const facebookChannels = [
		'facebook',
		'facebookPaid',
		'instagram',
		'instagramPaid',
	];
	const googleChannels = ['google', 'googlePMAX', 'googleDisplay'];

	const fbPageId = integrations?.social?.fb?.attributes?.pagesToPublishContent
		?.length
		? integrations.social.fb.attributes.pagesToPublishContent[0].id
		: undefined;

	const igName = integrations?.social?.fb?.attributes?.instagramPagesToPublish
		?.length
		? integrations.social.fb.attributes.instagramPagesToPublish[0].name
		: undefined;

	const handleCloseSuccessModal = () => {
		onCloseSuccessModal();
		setErrorMessages([]);
	};

	const handleInsufficientCreditsCancel = () => {
		onCloseInsufficientCreditsModal();
		setCheckoutUrl('');
		setAmountRequired(0);
	};

	const handleInsufficientCreditsContinue = () => {
		onCloseInsufficientCreditsModal();
		if (checkoutUrl) {
			externalRedirect(checkoutUrl);
		}
	};

	const handlePublishClick = () => {
		hasFinishedLaunchRef.current = false;
		isLaunchingRef.current = false;
		setIsPublishButtonLoading(true);

		// Add validation feedback
		if (!isValidCampaignConfig) {
			customToast(
				'Please complete the campaign configuration before publishing',
				'error',
			);
			setIsPublishButtonLoading(false);
			return;
		}

		if (!isValidCampaignBudget) {
			customToast(
				'Please set up the campaign budget before publishing',
				'error',
			);
			setIsPublishButtonLoading(false);
			return;
		}

		setTimeout(() => {
			startLaunch();
			setIsPublishButtonLoading(false);
		}, 500);
	};

	useEffect(() => {
		const count = countPendingCreativesByChannel(campaign, creatives);
		setPendingCreativesCount(count);
	}, [campaign, creatives, setPendingCreativesCount]);

	// Check if we should continue launch after payment
	useEffect(() => {
		const urlParams = new URLSearchParams(search);
		const shouldContinueLaunch = urlParams.get('continueLaunch') === 'true';

		if (shouldContinueLaunch && !continueLaunchInitiatedRef.current) {
			continueLaunchInitiatedRef.current = true;

			// Remove the parameter from URL
			urlParams.delete('continueLaunch');
			const newSearch = urlParams.toString();
			const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
			window.history.replaceState({}, '', newUrl);

			if (campaignId) {
				setOpenSections([false, false, true]);

				setTimeout(() => {
					startLaunchAfterPayment();
				}, 500);
			} else {
				// Try to get campaignId from URL as fallback
				const pathMatch = window.location.pathname.match(
					/\/projects\/campaigns\/([^/?]+)/,
				);
				const urlCampaignId = pathMatch ? pathMatch[1] : null;

				if (urlCampaignId) {
					setOpenSections([false, false, true]);

					setTimeout(() => {
						startLaunchAfterPayment(urlCampaignId);
					}, 500);
				} else {
					customToast(
						'Error continuing launch process. Please try again manually.',
						'error',
					);
				}
			}
		}
	}, [search]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				isLaunchInitiated &&
				publishButtonRef.current &&
				!publishButtonRef.current.contains(event.target as Node)
			) {
				setIsLaunchInitiated(false);
			}
		};

		if (isLaunchInitiated) {
			document.addEventListener('click', handleClickOutside);
		}

		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, [isLaunchInitiated]);

	const isValidCampaignSchedule = useMemo(() => {
		if (!campaign?.schedule) return false;
		return validateCampaignSchedule(campaign.schedule);
	}, [campaign]);

	const getChannelIdsFromCreative = (
		creative: IFailedCreative | IPublishedCreative,
	): string[] => {
		const channelIds: string[] = [];

		if (creative.channel === 'meta') {
			if (creative.placementId) {
				const placementChannel =
					placementIdToChannelIdMap[creative.placementId];
				if (placementChannel) channelIds.push(placementChannel);
			} else {
				channelIds.push('meta');
			}
		} else if (creative.channel === 'metaAds') {
			channelIds.push('facebookPaid', 'instagramPaid');
		} else if (creative.channel) {
			channelIds.push(creative.channel);
		}
		return channelIds;
	};

	const startLaunch = async () => {
		if (!campaign || !campaignId) return;

		const updatedCampaign = { ...campaign, channels: selectedChannels };
		setCampaign(updatedCampaign);

		if (!validateCampaign()) return;

		// Check credits before proceeding
		try {
			const campaignIdForApi = getCampaignId();
			if (!campaignIdForApi) {
				console.warn('Cannot check credits: campaign ID not available');
				return;
			}
			const creditCheckResponse = await checkCampaignCredits(campaignIdForApi);

			if (!creditCheckResponse.hasEnoughCredits) {
				if (creditCheckResponse.errorType === 'CAMPAIGN_LIMIT_REACHED') {
					setCampaignLimitData({
						currentCount: creditCheckResponse.currentCount,
						limit: creditCheckResponse.limit,
						errorMessage: creditCheckResponse.errorMessage,
						provider: creditCheckResponse.provider,
						planName: creditCheckResponse.planName,
						canUpgrade: creditCheckResponse.canUpgrade,
						upgradePrice: creditCheckResponse.upgradePrice,
						upgradeCurrency: creditCheckResponse.upgradeCurrency,
						nextPlanId: creditCheckResponse.nextPlanId,
					});
					onOpenCampaignLimitModal();
					return;
				}

				if (creditCheckResponse.checkoutUrl) {
					setCheckoutUrl(creditCheckResponse.checkoutUrl);
					setAmountRequired(creditCheckResponse.amountRequired || 0);
					onOpenInsufficientCreditsModal();
					return;
				} else {
					customToast('Insufficient credits to launch campaign', 'error');
					return;
				}
			}
		} catch (error) {
			console.error('Error checking credits:', error);
			customToast('Error checking credits. Please try again.', 'error');
			return;
		}

		setIsLaunchInitiated(true);

		if (!areAllDesignDirectionsWithCreatives() || isCreativesPending) {
			setShowCreativesLoader(true);
			try {
				await genrateCampaignCreatives(campaign, '', true);
				const campaignIdForApi = getCampaignId();
				if (!campaignIdForApi) {
					console.warn('Cannot fetch campaign: campaign ID not available');
					return;
				}
				const data = await getCampaign(campaignIdForApi);
				setCampaign(data);
				handlePendingCreatives();
			} catch (error) {
				console.error('Error generating creatives:', error);
				setShowCreativesLoader(false);
			}
		} else {
			await finishLaunch();
		}
	};

	const validateCampaign = (): boolean => {
		if (!isValidCampaignConfig) {
			handleInvalidCampaignConfig();
			return false;
		}

		if (!isValidCampaignSchedule) {
			handleInvalidCampaignSchedule();
			return false;
		}

		if (!isValidCampaignBudget) {
			return false;
		}

		if (isOneOffScheduleWithoutEndDate()) {
			setErrorMessages(['End Date is required for One-off campaigns']);
			return false;
		}

		return true;
	};

	useEffect(() => {
		if (isLaunchInitiated && pendingCreativesCount > 0) {
			handlePendingCreatives();
		}
	}, [pendingCreativesCount, isLaunchInitiated]);

	const handlePendingCreatives = async () => {
		const campaignIdForApi = getCampaignId();
		if (!campaignIdForApi || hasFinishedLaunchRef.current) {
			if (!campaignIdForApi) {
				console.warn(
					'Cannot handle pending creatives: campaign ID not available',
				);
			}
			return;
		}

		let attemptCount = 0;
		let updatedPendingCreativesCount =
			creatives?.filter(
				(creative) =>
					creative.status === 'pending' || creative.status === 'generating',
			).length || 0;

		while (
			updatedPendingCreativesCount > 0 ||
			!areAllDesignDirectionsWithCreatives()
		) {
			await delay(1000);
			const data = await getCampaign(campaignIdForApi);
			setCampaign(data);

			if (!data.creatives) return;

			updatedPendingCreativesCount = data.creatives.filter(
				(creative) =>
					creative.status === 'pending' || creative.status === 'generating',
			).length;

			attemptCount++;
			if (attemptCount >= 30) {
				if (campaign) {
					await genrateCampaignCreatives(campaign, '', true);
				}
				break;
			}
		}

		setShowCreativesLoader(false);
		if (!hasFinishedLaunchRef.current) {
			hasFinishedLaunchRef.current = true;
			await finishLaunch();
		}
	};

	const finishLaunch = async (placements?: string[]) => {
		// Use provided placements or fall back to campaign placements
		const placementsToUse = placements ?? campaign?.placements ?? [];

		const campaignIdForApi = getCampaignId();
		if (!campaignIdForApi) {
			throw new Error('Cannot launch: campaign ID not available');
		}

		setIsLaunching(true);
		onOpenLaunchingModal();

		try {
			let response: ILaunchResponse | null;

			if (placements) {
				// Use fresh placements directly with publishCampaignChannelsAndWait
				response = await publishCampaignChannelsAndWait(
					campaignIdForApi,
					placementsToUse,
				);
			} else {
				// Use the original onLaunch prop (for normal flow)
				response = await onLaunch();
			}

			handleLaunchResult(response);
		} catch (error: any) {
			handleLaunchError(error);
		} finally {
			onCloseLaunchingModal();
			onOpenSuccessModal();
			setIsLaunching(false);
		}
	};

	const handleInvalidCampaignConfig = () => {
		// Instead of automatically redirecting to step 1, show an error message
		// and let the user decide what to do
		setErrorMessages([
			'Campaign configuration is incomplete. Please review and complete all required fields before publishing.',
		]);

		// Optionally, you can still provide a way to go to step 1 if needed
		// but don't force the redirect
		console.log('⚠️ Campaign validation failed - configuration incomplete');
	};

	const handleInvalidCampaignSchedule = () => {
		const scheduleFormSubmitElement = document.getElementById(
			'campaign-schedule-form-submit',
		);
		scheduleFormSubmitElement?.click();
	};

	const isOneOffScheduleWithoutEndDate = () => {
		return (
			campaign?.schedule?.type === ECampaignScheduleType['One-off'] &&
			!campaign.schedule.endDate
		);
	};

	// Helper function to check if all design directions have creatives (reusable for fresh data)
	const checkAllDesignDirectionsWithCreatives = (
		designDirections: any[],
		creatives: any[],
	) => {
		if (!designDirections || !creatives) {
			return false;
		}

		const designDirectionIds = new Set(
			designDirections.map((direction: any) => direction.id),
		);

		const creativeDesignDirectionIds = new Set(
			creatives.map((creative: any) => creative.designDirectionId),
		);

		let allDirectionsWithCreatives = true;
		designDirectionIds.forEach((id) => {
			if (!creativeDesignDirectionIds.has(id)) {
				allDirectionsWithCreatives = false;
			}
		});

		return allDirectionsWithCreatives;
	};

	const areAllDesignDirectionsWithCreatives = () => {
		return checkAllDesignDirectionsWithCreatives(designDirections, creatives);
	};

	// Unified function for launching after payment (combines both previous functions)
	const startLaunchAfterPayment = async (forceId?: string) => {
		// Use forceId if provided, otherwise get campaign ID from campaign object
		const campaignIdToUse = forceId || getCampaignId();

		if (!campaignIdToUse) {
			return;
		}

		try {
			const freshCampaignData = await getCampaign(campaignIdToUse);

			setCampaign(freshCampaignData);

			// Wait for context to update
			await delay(300);

			// Verify the campaign was loaded with placements
			if (!freshCampaignData.placements?.length) {
				customToast(
					'Campaign data not loaded properly. Please try launching manually.',
					'error',
				);
				return;
			}

			const updatedCampaign = {
				...freshCampaignData,
				channels: selectedChannels,
			};
			setCampaign(updatedCampaign);
			await delay(200);

			setIsLaunchInitiated(true);

			// Use the fresh data from the loaded campaign
			const freshDesignDirections = freshCampaignData.designDirections || [];
			const freshCreatives = freshCampaignData.creatives || [];

			const needsCreatives =
				!checkAllDesignDirectionsWithCreatives(
					freshDesignDirections,
					freshCreatives,
				) ||
				freshCreatives.some(
					(c: any) => c.status === 'pending' || c.status === 'generating',
				);

			if (needsCreatives) {
				setShowCreativesLoader(true);
				try {
					await genrateCampaignCreatives(freshCampaignData, '', true);
					const data = await getCampaign(campaignIdToUse);
					setCampaign(data);
					handlePendingCreatives();
				} catch (error) {
					console.error('Error generating creatives:', error);
					setShowCreativesLoader(false);
				}
			} else {
				await finishLaunch(freshCampaignData.placements || []);
			}
		} catch (error) {
			console.error('Error loading campaign data:', error);
			customToast(
				'Error loading campaign data. Please refresh the page and try again.',
				'error',
			);
			return;
		}
	};

	const handleLaunchResult = (response: ILaunchResponse | null) => {
		if (!response) {
			setModalTitle('ERROR');
			setFailedCreatives(
				selectedChannels.map((channel) => ({
					channel,
					errors: [
						{ message: "We couldn't publish this ad, please try again" },
					],
					status: 'failed',
				})),
			);
			setPublishedCreatives([]);
			return;
		}

		const publishedCreativesData = response.output?.publishedCreatives || [];
		const failedCreativesData = response.output?.failedCreatives || [];

		setPublishedCreatives(publishedCreativesData);
		setFailedCreatives(failedCreativesData);

		if (failedCreativesData.length > 0 && publishedCreativesData.length === 0) {
			setModalTitle('ERROR');
		} else if (
			failedCreativesData.length > 0 &&
			publishedCreativesData.length > 0
		) {
			setModalTitle('PARTIAL PUBLISHED');
		} else {
			setModalTitle('SUCCESS');
		}
	};

	const handleLaunchError = (error: any) => {
		if (error?.response?.status >= 500 || error?.response?.statusCode >= 500) {
			setModalTitle('ERROR');
			setFailedCreatives(
				selectedChannels.map((channel) => ({
					channel,
					errors: [
						{ message: "We couldn't publish this ad, please try again" },
					],
					status: 'failed',
				})),
			);
			setPublishedCreatives([]);
		} else {
			const backendResult = error?.response?.data
				?.result as ILaunchResponse['output'];
			const backendMessage = error?.response?.data?.message as
				| string
				| undefined;
			const backendFailedCreatives = backendResult?.failedCreatives || [];
			const backendPublishedCreatives = backendResult?.publishedCreatives || [];

			setFailedCreatives(backendFailedCreatives);
			setPublishedCreatives(backendPublishedCreatives);

			if (
				backendFailedCreatives.length > 0 &&
				backendPublishedCreatives.length > 0
			) {
				setModalTitle('PARTIAL PUBLISHED');
				if (backendMessage) {
					const messagesArray = backendMessage.split('|');
					setErrorMessages(messagesArray);
				} else {
					const messagesArray = backendFailedCreatives.flatMap(
						(fc: IFailedCreative) => fc.errors.map((e: IError) => e.message),
					);
					setErrorMessages(messagesArray);
				}
			} else {
				setModalTitle('ERROR');
				if (backendMessage) {
					const messagesArray = backendMessage.split('|');
					setErrorMessages(messagesArray);
				} else {
					const messagesArray = backendFailedCreatives.flatMap(
						(fc: IFailedCreative) => fc.errors.map((e: IError) => e.message),
					);
					setErrorMessages(messagesArray);
				}
			}
		}
	};

	return (
		<>
			<LaunchingCampaignModal isOpen={isOpenLaunchingModal} />
			<CampaignSuccessModal
				isOpen={isOpenSuccessModal}
				onClose={handleCloseSuccessModal}
				title={modalTitle}
				failedCreatives={failedCreatives}
				publishedCreatives={publishedCreatives}
				selectedChannels={selectedChannels}
				campaignName={campaign?.description || ''}
				showFacebookLinks={
					Array.isArray(selectedChannels) &&
					selectedChannels.some((ch) => facebookChannels.includes(ch))
				}
				showGoogleLinks={
					Array.isArray(selectedChannels) &&
					selectedChannels.some((ch) => googleChannels.includes(ch))
				}
				fbPageId={fbPageId}
				igName={igName}
			/>

			<InsufficientCreditsModal
				isOpen={isOpenInsufficientCreditsModal}
				onClose={handleInsufficientCreditsCancel}
				onContinue={handleInsufficientCreditsContinue}
				amountRequired={amountRequired}
			/>

			<CampaignLimitModal
				isOpen={isOpenCampaignLimitModal}
				onClose={onCloseCampaignLimitModal}
				currentCount={campaignLimitData.currentCount}
				limit={campaignLimitData.limit}
				errorMessage={campaignLimitData.errorMessage}
				provider={campaignLimitData.provider}
				planName={campaignLimitData.planName}
				canUpgrade={campaignLimitData.canUpgrade}
				upgradePrice={campaignLimitData.upgradePrice}
				upgradeCurrency={campaignLimitData.upgradeCurrency}
				nextPlanId={campaignLimitData.nextPlanId}
				onUpgradeSuccess={() => {
					// Retry publishing after successful upgrade
					startLaunch();
				}}
			/>

			<Container maxW="full" py={6} m={0} px={0}>
				<Flex justifyContent={{ base: 'stretch', md: 'right' }} alignItems="flex-end">
					<Button
						ref={publishButtonRef}
						variant="orangeSolid"
						_hover={{ opacity: 0.9 }}
						_disabled={{ opacity: 0.7, cursor: 'not-allowed' }}
						onClick={handleSubmit(handlePublishClick)}
						isDisabled={isDesignDirectionPending || isPublishButtonLoading}
						isLoading={isPublishButtonLoading}
						w={{ base: 'full', md: 'auto' }}
					>
						Publish to the channels
					</Button>
				</Flex>
			</Container>
		</>
	);
};

export default LaunchCampaign;
